import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import {
  bulandi2026Meta,
  bulandiRegistrationUpiQrUrl,
} from "../../data/bulandi2026Data";
import { GENDER_OPTIONS } from "../../constants/formOptions";
import { BULANDI_OFFICIAL_WHATSAPP_GROUP_URL } from "../../config/whatsappMessaging";
import {
  toastActionReceived,
  toastRegistrationSuccess,
  toastRegistrationError,
  dismissRegistrationToast,
} from "../../utils/registrationToasts";
import {
  BULANDI_REG_DOB_INPUT_MAX,
  BULANDI_REG_DOB_INPUT_MIN,
  bulandiRegFieldError,
  clampBulandiRegistrationDobValue,
  readFileAsBase64,
} from "../../utils/bulandiRegistrationFormUtils";
const inputClass =
  "mt-1.5 w-full rounded-xl border-2 border-bul-border bg-bul-bg px-3 py-2.5 font-mono text-sm text-bul-fg placeholder:text-bul-mutedfg focus:border-bul-lime focus:outline-none focus:ring-2 focus:ring-bul-lime/25";
const labelClass =
  "block font-mono text-xs font-bold uppercase tracking-wider text-bul-mutedfg";
export function BulandiRegistrationDrawer({ open, onClose, onRegistered }) {
  const submitLockRef = useRef(false);
  const [name, setName] = useState("");
  const [parentName, setParentName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneAlt, setPhoneAlt] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [paymentFile, setPaymentFile] = useState(null);
  const [aadharAcknowledged, setAadharAcknowledged] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  useEffect(() => {
    if (!open) {
      setName("");
      setParentName("");
      setPhone("");
      setPhoneAlt("");
      setGender("");
      setDob("");
      setEmail("");
      setPaymentFile(null);
      setAadharAcknowledged(false);
      setErrors({});
      setSubmitting(false);
      setSubmitError("");
    }
  }, [open]);
  const applyFieldError = (field, value) => {
    const msg = bulandiRegFieldError[field](value);
    setErrors((prev) => {
      const next = { ...prev };
      if (msg) next[field] = msg;
      else delete next[field];
      return next;
    });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const next = {};
    const fields = [
      ["name", name],
      ["parentName", parentName],
      ["phone", phone],
      ["phoneAlt", phoneAlt],
      ["gender", gender],
      ["dob", dob],
      ["email", email],
      ["paymentFile", paymentFile],
    ];
    for (const [key, val] of fields) {
      const msg = bulandiRegFieldError[key](val);
      if (msg) next[key] = msg;
    }
    if (!aadharAcknowledged) {
      next.aadharAcknowledged =
        "Please confirm you will bring Aadhaar proof on the event day for verification at the registration desk.";
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      const firstErr = Object.values(next).find(Boolean);
      if (firstErr) toastRegistrationError("bulandi", firstErr);
      return;
    }
    if (bulandi2026Meta.mainRegistrationOpen === false) {
      const closedLabel =
        (bulandi2026Meta.mainRegistrationClosedLabel || "").trim() ||
        "Registration closed";
      toastRegistrationError(
        "bulandi",
        `${closedLabel}. If you already have a B No., use Events Sign In.`,
      );
      return;
    }
    const webAppUrl = (bulandi2026Meta.registrationWebAppUrl || "").trim();
    if (!webAppUrl) {
      toastRegistrationError(
        "bulandi",
        "Registration is not configured. Set registrationWebAppUrl in src/data/bulandi2026Data.js (Google Apps Script web app URL).",
      );
      return;
    }
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    let tid = null;
    try {
      setSubmitting(true);
      const file = paymentFile;
      const paymentFileBase64 = await readFileAsBase64(file);
      const safeName = file.name.replace(/[^\w.\- ]+/g, "_").slice(0, 180);
      const nameT = name.trim();
      const parentT = parentName.trim();
      const phoneSnap = phone;
      const phoneAlT = phoneAlt.trim();
      const genderSnap = gender;
      const dobSnap = dob;
      const mailT = email.trim();
      tid = toastActionReceived("Bulandi registration");
      onClose();
      const payload = {
        name: nameT,
        parentName: parentT,
        whatsappNo: phoneSnap,
        phoneAlternate: phoneAlT,
        gender: genderSnap,
        dob: dobSnap,
        mail: mailT,
        paymentFileName: safeName || "payment.jpg",
        paymentMimeType: file.type || "image/jpeg",
        paymentFileBase64,
      };
      const regSecret = (bulandi2026Meta.registrationSubmitSecret || "").trim();
      if (regSecret) {
        payload.secret = regSecret;
      }
      const res = await fetch(webAppUrl, {
        method: "POST",
        mode: "cors",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "text/plain;charset=utf-8" },
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(
          "Unexpected server response. Check the Apps Script deployment URL and that the script is deployed as a web app.",
        );
      }
      if (!data.ok) {
        throw new Error(data.error || "Registration failed.");
      }
      if (!data.brNumber) {
        throw new Error("No B No. returned from the server.");
      }
      const br = String(data.brNumber);
      onRegistered({ brNumber: br, dob: dobSnap });
      dismissRegistrationToast(tid);
      toastRegistrationSuccess("bulandi", {
        whatsappGroupUrl: BULANDI_OFFICIAL_WHATSAPP_GROUP_URL,
      });
    } catch (err) {
      dismissRegistrationToast(tid);
      const msg = err?.message || "Something went wrong. Please try again.";
      setSubmitError(msg);
      toastRegistrationError("bulandi", msg);
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };
  if (!open) return null;
  return React.createElement(
    "div",
    {
      className: "fixed inset-0 z-[60] flex justify-end bg-black/70 backdrop-blur-sm",
      onClick: onClose,
      role: "presentation",
    },
    React.createElement(
      "aside",
      {
        className:
          "flex h-full w-full max-w-md flex-col border-l-2 border-bul-border bg-bul-bg shadow-bul-brutal motion-safe:transition-transform motion-safe:duration-300",
        onClick: (e) => e.stopPropagation(),
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": "bulandi-reg-drawer-title",
      },
      React.createElement(
        "div",
        {
          className:
            "relative flex items-center justify-between gap-3 overflow-hidden border-b border-bul-border bg-bul-surface/90 px-5 py-5 sm:px-6",
        },
        React.createElement(
          "div",
          { className: "relative min-w-0" },
          React.createElement(
            "p",
            {
              className:
                "font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-bul-lime",
            },
            "Bulandi 2026",
          ),
          React.createElement(
            "h2",
            {
              id: "bulandi-reg-drawer-title",
              className:
                "mt-1 font-display text-[0.9375rem] uppercase tracking-tight text-bul-fg",
            },
            "Registration",
          ),
          React.createElement("p", {
            className: "mt-1 text-xs font-medium leading-snug text-bul-mutedfg",
          }),
        ),
        React.createElement(
          "button",
          {
            type: "button",
            onClick: onClose,
            className:
              "relative shrink-0 rounded-full p-2 text-bul-mutedfg transition hover:bg-bul-border/50 hover:text-bul-lime focus:outline-none focus-visible:ring-2 focus-visible:ring-bul-lime focus-visible:ring-offset-2 focus-visible:ring-offset-bul-bg",
            "aria-label": "Close registration form",
          },
          React.createElement(X, { size: 22 }),
        ),
      ),
      React.createElement(
        "form",
        { onSubmit: handleSubmit, className: "flex min-h-0 flex-1 flex-col" },
        React.createElement(
          "div",
          {
            className:
              "min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5 space-y-4",
          },
          React.createElement(
            "div",
            {
              className:
                "rounded-2xl border border-bul-lime/30 bg-bul-lime/10 px-4 py-3.5 text-sm text-bul-fg shadow-bul-brutal-sm",
              role: "note",
              "aria-label": "Registration eligibility",
            },
            React.createElement(
              "p",
              {
                className:
                  "font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-bul-lime",
              },
              "Eligibility",
            ),
            React.createElement(
              "p",
              { className: "mt-2 leading-relaxed text-bul-mutedfg" },
              "Registration is intended for Maheshwaris in ",
              React.createElement("span", { className: "font-bold" }, "Tamil Nadu"),
              ",",
              " ",
              React.createElement("span", { className: "font-bold" }, "Kerala"),
              ", and",
              " ",
              React.createElement(
                "span",
                { className: "font-bold" },
                "Puducherry (Pondicherry)",
              ),
              " only.",
            ),
          ),
          React.createElement(
            "div",
            null,
            React.createElement(
              "label",
              { htmlFor: "bulandi-reg-name", className: labelClass },
              "Name ",
              React.createElement("span", { className: "text-bul-lime" }, "*"),
            ),
            React.createElement("input", {
              id: "bulandi-reg-name",
              type: "text",
              autoComplete: "name",
              value: name,
              onChange: (e) => {
                const v = e.target.value;
                setName(v);
                applyFieldError("name", v);
              },
              onBlur: (e) => applyFieldError("name", e.target.value),
              className: inputClass,
              "aria-invalid": errors.name ? "true" : "false",
            }),
            errors.name &&
              React.createElement(
                "p",
                { className: "mt-1 text-xs text-red-300" },
                errors.name,
              ),
          ),
          React.createElement(
            "div",
            null,
            React.createElement(
              "label",
              { htmlFor: "bulandi-reg-parent-name", className: labelClass },
              "Parent Name ",
              React.createElement("span", { className: "text-bul-lime" }, "*"),
            ),
            React.createElement("input", {
              id: "bulandi-reg-parent-name",
              type: "text",
              autoComplete: "additional-name",
              value: parentName,
              onChange: (e) => {
                const v = e.target.value;
                setParentName(v);
                applyFieldError("parentName", v);
              },
              onBlur: (e) => applyFieldError("parentName", e.target.value),
              className: inputClass,
              "aria-invalid": errors.parentName ? "true" : "false",
            }),
            errors.parentName &&
              React.createElement(
                "p",
                { className: "mt-1 text-xs text-red-300" },
                errors.parentName,
              ),
          ),
          React.createElement(
            "div",
            null,
            React.createElement(
              "label",
              { htmlFor: "bulandi-reg-phone", className: labelClass },
              "Phone (WhatsApp only) ",
              React.createElement("span", { className: "text-bul-lime" }, "*"),
            ),
            React.createElement("input", {
              id: "bulandi-reg-phone",
              type: "tel",
              inputMode: "numeric",
              autoComplete: "tel",
              maxLength: 10,
              placeholder: "10-digit number",
              value: phone,
              onChange: (e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                setPhone(v);
                applyFieldError("phone", v);
              },
              onBlur: (e) => applyFieldError("phone", e.target.value),
              className: `${inputClass} tabular-nums`,
              "aria-invalid": errors.phone ? "true" : "false",
            }),
            errors.phone &&
              React.createElement(
                "p",
                { className: "mt-1 text-xs text-red-300" },
                errors.phone,
              ),
          ),
          React.createElement(
            "div",
            null,
            React.createElement(
              "label",
              { htmlFor: "bulandi-reg-phone-alt", className: labelClass },
              "Phone 2 (alternative, optional)",
            ),
            React.createElement("input", {
              id: "bulandi-reg-phone-alt",
              type: "tel",
              inputMode: "numeric",
              autoComplete: "tel",
              maxLength: 10,
              placeholder: "10-digit number",
              value: phoneAlt,
              onChange: (e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                setPhoneAlt(v);
                applyFieldError("phoneAlt", v);
              },
              onBlur: (e) => applyFieldError("phoneAlt", e.target.value),
              className: `${inputClass} tabular-nums`,
              "aria-invalid": errors.phoneAlt ? "true" : "false",
            }),
            errors.phoneAlt &&
              React.createElement(
                "p",
                { className: "mt-1 text-xs text-red-300" },
                errors.phoneAlt,
              ),
          ),
          React.createElement(
            "div",
            null,
            React.createElement(
              "label",
              { htmlFor: "bulandi-reg-gender", className: labelClass },
              "Gender ",
              React.createElement("span", { className: "text-bul-lime" }, "*"),
            ),
            React.createElement(
              "select",
              {
                id: "bulandi-reg-gender",
                value: gender,
                onChange: (e) => {
                  const v = e.target.value;
                  setGender(v);
                  applyFieldError("gender", v);
                },
                onBlur: (e) => applyFieldError("gender", e.target.value),
                className: inputClass,
                "aria-invalid": errors.gender ? "true" : "false",
              },
              GENDER_OPTIONS.map((opt) =>
                React.createElement(
                  "option",
                  {
                    key: opt.value || "empty",
                    value: opt.value,
                    className: "bg-bul-bg text-bul-fg",
                  },
                  opt.label,
                ),
              ),
            ),
            errors.gender &&
              React.createElement(
                "p",
                { className: "mt-1 text-xs text-red-300" },
                errors.gender,
              ),
          ),
          React.createElement(
            "div",
            null,
            React.createElement(
              "label",
              { htmlFor: "bulandi-reg-dob", className: labelClass },
              "Date of birth ",
              React.createElement("span", { className: "text-bul-lime" }, "*"),
            ),
            React.createElement("input", {
              id: "bulandi-reg-dob",
              type: "date",
              min: BULANDI_REG_DOB_INPUT_MIN,
              max: BULANDI_REG_DOB_INPUT_MAX,
              value: dob,
              onChange: (e) => {
                const v = e.target.value;
                setDob(v);
                applyFieldError("dob", v);
              },
              onBlur: (e) => {
                const v = clampBulandiRegistrationDobValue(e.target.value.trim());
                if (v !== e.target.value.trim()) setDob(v);
                applyFieldError("dob", v);
              },
              className: inputClass,
              "aria-invalid": errors.dob ? "true" : "false",
            }),
            errors.dob &&
              React.createElement(
                "p",
                { className: "mt-1 text-xs text-red-300" },
                errors.dob,
              ),
          ),
          React.createElement(
            "div",
            null,
            React.createElement(
              "label",
              { htmlFor: "bulandi-reg-email", className: labelClass },
              "Email ",
              React.createElement("span", { className: "text-bul-lime" }, "*"),
            ),
            React.createElement("input", {
              id: "bulandi-reg-email",
              type: "email",
              autoComplete: "email",
              value: email,
              onChange: (e) => {
                const v = e.target.value;
                setEmail(v);
                applyFieldError("email", v);
              },
              onBlur: (e) => applyFieldError("email", e.target.value),
              className: inputClass,
              "aria-invalid": errors.email ? "true" : "false",
            }),
            errors.email &&
              React.createElement(
                "p",
                { className: "mt-1 text-xs text-red-300" },
                errors.email,
              ),
          ),
          React.createElement(
            "div",
            {
              className:
                "space-y-3 rounded-xl border-2 border-bul-border bg-bul-surface/80 p-4",
            },
            React.createElement(
              "h3",
              {
                className:
                  "font-display text-xs uppercase tracking-wide text-bul-fg sm:text-[0.75rem]",
              },
              "Payment details",
            ),
            React.createElement(
              "p",
              { className: "text-sm font-bold text-bul-fg" },
              "Registration fees: ",
              React.createElement(
                "span",
                { className: "tabular-nums text-bul-lime" },
                "\u20B9500",
              ),
            ),
            React.createElement(
              "p",
              {
                className:
                  "rounded-lg border border-bul-lime/30 bg-bul-lime/10 px-3 py-2 text-xs font-bold text-bul-fg",
              },
              "No cash \u2014 pay only via UPI using the QR code below, then upload your payment screenshot.",
            ),
            React.createElement(
              "p",
              { className: "text-xs leading-snug text-bul-mutedfg" },
              "For any payment issues, please WhatsApp",
              " ",
              React.createElement(
                "a",
                {
                  href: "https://wa.me/918610447053",
                  target: "_blank",
                  rel: "noopener noreferrer",
                  className:
                    "font-bold text-bul-lime underline decoration-bul-lime/50 underline-offset-2 hover:text-bul-cyan",
                },
                "8610447053",
              ),
              ".",
            ),
            React.createElement(
              "div",
              null,
              React.createElement(
                "p",
                {
                  className:
                    "text-[10px] font-bold uppercase tracking-[0.25em] text-bul-lime mb-2",
                },
                "UPI QR",
              ),
              React.createElement(
                "div",
                {
                  className:
                    "flex justify-center rounded-lg border-2 border-bul-border bg-transparent p-3",
                },
                React.createElement("img", {
                  src: bulandiRegistrationUpiQrUrl.trim(),
                  alt: "SMYM UPI QR code for payment",
                  className: "max-h-48 w-auto max-w-full object-contain",
                }),
              ),
            ),
            React.createElement(
              "div",
              null,
              React.createElement(
                "label",
                { htmlFor: "bulandi-reg-payment", className: labelClass },
                "Payment screenshot ",
                React.createElement("span", { className: "text-bul-lime" }, "*"),
              ),
              React.createElement("input", {
                id: "bulandi-reg-payment",
                type: "file",
                accept: "image/*",
                onChange: (e) => {
                  const f = e.target.files?.[0] ?? null;
                  setPaymentFile(f);
                  applyFieldError("paymentFile", f);
                },
                onBlur: () => applyFieldError("paymentFile", paymentFile),
                className:
                  "mt-1.5 block w-full text-sm text-bul-mutedfg file:mr-3 file:rounded-lg file:border-2 file:border-[color:var(--bul-strong-outline)] file:bg-bul-lime file:px-3 file:py-2 file:font-mono file:text-sm file:font-bold file:uppercase file:tracking-wider file:text-[rgb(var(--bul-primary-cta-text-rgb))] hover:file:brightness-110",
                "aria-invalid": errors.paymentFile ? "true" : "false",
              }),
              errors.paymentFile &&
                React.createElement(
                  "p",
                  { className: "mt-1 text-xs text-red-300" },
                  errors.paymentFile,
                ),
            ),
          ),
        ),
        React.createElement(
          "div",
          {
            className:
              "shrink-0 space-y-3 border-t border-bul-border bg-bul-surface/95 p-4 backdrop-blur-md sm:p-5",
          },
          React.createElement(
            "div",
            {
              className: `rounded-xl border-2 px-3 py-3 ${errors.aadharAcknowledged ? "border-red-500/60 bg-red-950/40" : "border-bul-border bg-bul-bg/70"}`,
            },
            React.createElement(
              "div",
              { className: "flex items-start gap-3" },
              React.createElement("input", {
                id: "bulandi-reg-aadhar-ack",
                type: "checkbox",
                checked: aadharAcknowledged,
                onChange: (e) => {
                  const checked = e.target.checked;
                  setAadharAcknowledged(checked);
                  setErrors((prev) => {
                    const n = { ...prev };
                    if (checked) delete n.aadharAcknowledged;
                    return n;
                  });
                },
                className:
                  "mt-0.5 h-4 w-4 shrink-0 rounded border-bul-border bg-bul-bg text-bul-lime focus:ring-bul-lime",
                "aria-invalid": errors.aadharAcknowledged ? "true" : "false",
                "aria-describedby": errors.aadharAcknowledged
                  ? "bulandi-reg-aadhar-ack-error"
                  : undefined,
              }),
              React.createElement(
                "label",
                {
                  htmlFor: "bulandi-reg-aadhar-ack",
                  className:
                    "cursor-pointer select-none text-sm leading-snug text-bul-mutedfg",
                },
                React.createElement(
                  "span",
                  { className: "font-bold text-bul-fg" },
                  "Required:",
                ),
                " I will carry my",
                " ",
                React.createElement(
                  "span",
                  { className: "font-bold" },
                  "Aadhaar proof",
                ),
                " on the day of the event for verification at the registration desk. ",
                React.createElement("span", { className: "text-bul-lime" }, "*"),
              ),
            ),
            errors.aadharAcknowledged
              ? React.createElement(
                  "p",
                  {
                    id: "bulandi-reg-aadhar-ack-error",
                    className: "mt-2 text-xs text-red-200 pl-7",
                    role: "alert",
                  },
                  errors.aadharAcknowledged,
                )
              : null,
          ),
          submitError &&
            React.createElement(
              "p",
              {
                className:
                  "text-sm text-red-200 bg-red-950/60 border border-red-700/60 rounded-lg px-3 py-2",
                role: "alert",
              },
              submitError,
            ),
          React.createElement(
            "button",
            {
              type: "submit",
              disabled: submitting,
              className: "bul-cta-primary relative w-full py-3.5 text-base",
            },
            submitting ? "Submitting\u2026" : "Submit",
          ),
        ),
      ),
    ),
  );
}
